import { useCallback, useEffect, useRef, useState, type HTMLAttributes } from "react";

type PickerRowKey<T extends object> = string | ((record: T) => string | number);

type PickerRowDoubleClickOptions<T extends object> = {
  open: boolean;
  rowKey: PickerRowKey<T>;
  rowSelectionType: "radio" | "checkbox";
  selectedRowKeys: readonly (string | number)[];
  onSelectionChange?: (keys: Array<string | number>, rows: T[]) => void;
  getCheckboxProps?: (record: T) => { disabled?: boolean };
  onSubmit?: () => Promise<boolean> | boolean;
  onClose: () => void;
  blocked?: boolean;
};

type PickerRowOnRow<T extends object> = (
  record: T,
  index?: number,
) => HTMLAttributes<HTMLTableRowElement>;

function readPickerRowKey<T extends object>(record: T, rowKey: PickerRowKey<T>) {
  const value = typeof rowKey === "function" ? rowKey(record) : Reflect.get(record, rowKey);
  if (typeof value !== "string" && typeof value !== "number") {
    throw new Error(`PICKER_ROW_KEY_INVALID: ${rowKey} must resolve to a string or number`);
  }
  return value;
}

export function withPickerRowDoubleClick<T extends object>(
  onRow: PickerRowOnRow<T> | undefined,
  onDoubleClick: (record: T) => void,
): PickerRowOnRow<T> {
  return (record, index) => {
    const rowProps = onRow?.(record, index) ?? {};
    return {
      ...rowProps,
      onDoubleClick: (event) => {
        rowProps.onDoubleClick?.(event);
        if (!event.defaultPrevented) {
          onDoubleClick(record);
        }
      },
    };
  };
}

export function usePickerRowDoubleClick<T extends object>({
  open,
  rowKey,
  rowSelectionType,
  selectedRowKeys,
  onSelectionChange,
  getCheckboxProps,
  onSubmit,
  onClose,
  blocked = false,
}: PickerRowDoubleClickOptions<T>) {
  const [pendingRowKey, setPendingRowKey] = useState<string | number | null>(null);
  const submittingRef = useRef(false);

  useEffect(() => {
    if (!open) {
      setPendingRowKey(null);
    }
  }, [open]);

  useEffect(() => {
    if (
      !open ||
      blocked ||
      pendingRowKey === null ||
      !onSubmit ||
      submittingRef.current ||
      !selectedRowKeys.includes(pendingRowKey)
    ) {
      return;
    }

    setPendingRowKey(null);
    submittingRef.current = true;
    void Promise.resolve(onSubmit())
      .then((shouldClose) => {
        if (shouldClose) {
          onClose();
        }
      })
      .finally(() => {
        submittingRef.current = false;
      });
  }, [blocked, onClose, onSubmit, open, pendingRowKey, selectedRowKeys]);

  const onRowDoubleClick = useCallback(
    (record: T) => {
      if (
        !open ||
        blocked ||
        submittingRef.current ||
        rowSelectionType !== "radio" ||
        !onSelectionChange ||
        !onSubmit ||
        getCheckboxProps?.(record).disabled
      ) {
        return;
      }

      const key = readPickerRowKey(record, rowKey);
      onSelectionChange([key], [record]);
      setPendingRowKey(key);
    },
    [blocked, getCheckboxProps, onSelectionChange, onSubmit, open, rowKey, rowSelectionType],
  );

  return {
    enabled:
      rowSelectionType === "radio" && onSelectionChange !== undefined && onSubmit !== undefined,
    onRowDoubleClick,
  };
}
